import {
  AlertTriangle,
  BarChart3,
  Download,
  FileClock,
  Search,
  TrendingUp,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import { useWorkspace } from "../context/WorkspaceContext";
import type { BalanceSummary } from "../types/api";
import { formatDate, formatMinutes } from "../utils/format";

type EmployeeRow = {
  membership_id: number;
  user_id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  contract_minutes_per_week: number;
  work_schedule_minutes_per_week: number;
  department: string;
  location: string;
  managers: string[];
  balances: BalanceSummary[];
};

type ReportData = {
  year: number;
  leave_usage_per_month: Array<{ month: number; minutes: number; hours: number }>;
  leave_by_type: Array<{
    leave_type: string;
    category: string;
    minutes: number;
    hours: number;
  }>;
  open_requests: number;
  expiring_transport: Array<{
    id: number;
    employee: string;
    leave_type: string;
    remaining_minutes: number;
    expires_on: string;
  }>;
  absence_by_team: Array<{ team: string; minutes: number; hours: number }>;
  average_leave_minutes: number;
  average_leave_hours: number;
};

const months = [
  "jan",
  "feb",
  "mrt",
  "apr",
  "mei",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
];

export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { organizationId, organization, capabilities } = useWorkspace();
  const [view, setView] = useState(
    searchParams.get("view") === "reports" ? "reports" : "employees",
  );
  const [year, setYear] = useState(new Date().getFullYear());
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [reports, setReports] = useState<ReportData | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!organizationId || !capabilities.canViewOrganizationReports) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    void Promise.all([
      api.get(`/organizations/${organizationId}/employees/?year=${year}`),
      api.get<ReportData>(`/organizations/${organizationId}/reports/?year=${year}`),
    ])
      .then(([employeeResponse, reportResponse]) => {
        setEmployees(employeeResponse.data.results);
        setReports(reportResponse.data);
      })
      .catch(() => setError("De beheerinformatie kon niet worden geladen."))
      .finally(() => setLoading(false));
  }, [organizationId, capabilities.canViewOrganizationReports, year]);

  const visibleEmployees = useMemo(() => {
    const query = search.trim().toLowerCase();
    return employees.filter(
      (employee) =>
        !query ||
        `${employee.name} ${employee.email} ${employee.department}`
          .toLowerCase()
          .includes(query),
    );
  }, [employees, search]);

  const setActiveView = (next: "employees" | "reports") => {
    setView(next);
    setSearchParams(next === "reports" ? { view: "reports" } : {});
  };

  const exportEmployees = () => {
    const header = [
      "Naam",
      "E-mail",
      "Contracturen",
      "Verlofsaldo",
      "Aangevraagd",
      "Manager",
      "Status",
    ];
    const rows = employees.map((employee) => {
      const balance =
        employee.balances.find((row) => row.category === "leave") ??
        employee.balances[0];
      return [
        employee.name,
        employee.email,
        employee.contract_minutes_per_week / 60,
        balance?.current_hours ?? 0,
        balance?.requested_hours ?? 0,
        employee.managers.join(" / "),
        employee.status,
      ];
    });
    const csv = [header, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(";"),
      )
      .join("\r\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `verlofkaart-medewerkers-${year}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!organizationId || !organization) {
    return (
      <div>
        <PageHeader
          title="Medewerkers & rapportages"
          description="Selecteer een organisatie om beheerinformatie te bekijken."
        />
        <section className="upgrade-panel">
          <Users size={28} />
          <h2>Beschikbaar in Team en Business</h2>
          <p>Rapporteer over verlofgebruik, bezetting en transport.</p>
          <Link className="button primary" to="/app/settings/abonnement">
            Pakket bekijken
          </Link>
        </section>
      </div>
    );
  }
  if (!capabilities.canViewOrganizationReports) {
    return (
      <div>
        <PageHeader title="Medewerkers & rapportages" />
        <ErrorNotice>Je rol geeft geen toegang tot organisatierapportages.</ErrorNotice>
      </div>
    );
  }
  if (loading) return <LoadingState label="Rapportages laden..." />;

  const maxMonthly = Math.max(
    ...(reports?.leave_usage_per_month.map((row) => row.minutes) ?? [1]),
    1,
  );

  return (
    <div className="reports-page">
      <PageHeader
        eyebrow={organization.name}
        title="Medewerkers & rapportages"
        description="Saldi, werkpatronen en verlofgebruik op organisatieniveau."
        actions={
          <div className="year-and-export">
            <select
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
              aria-label="Rapportagejaar"
            >
              {[year - 1, year, year + 1].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <button className="button subtle" onClick={exportEmployees}>
              <Download size={17} /> Exporteren
            </button>
          </div>
        }
      />
      {error && <ErrorNotice>{error}</ErrorNotice>}

      <div className="section-tabs">
        <button
          className={view === "employees" ? "active" : ""}
          onClick={() => setActiveView("employees")}
        >
          Medewerkers <span>{employees.length}</span>
        </button>
        <button
          className={view === "reports" ? "active" : ""}
          onClick={() => setActiveView("reports")}
        >
          Rapportages
        </button>
      </div>

      {view === "employees" && (
        <>
          <div className="list-toolbar align-right">
            <label className="search-field">
              <Search size={17} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Zoek medewerker"
              />
            </label>
          </div>
          <section className="surface table-scroll">
            <table className="data-table employee-table">
              <thead>
                <tr>
                  <th>Medewerker</th>
                  <th>Contract</th>
                  <th>Verlofsaldo</th>
                  <th>ATV</th>
                  <th>Transport</th>
                  <th>Gepland</th>
                  <th>In afwachting</th>
                  <th>Manager</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleEmployees.map((employee) => {
                  const leave = employee.balances.find(
                    (row) => row.category === "leave",
                  );
                  const atvMinutes = employee.balances
                    .filter((row) => row.category === "atv")
                    .reduce((sum, row) => sum + row.current_minutes, 0);
                  return (
                    <tr key={employee.membership_id}>
                      <td>
                        <div className="employee-name">
                          <span className="avatar mini">
                            {employee.name.slice(0, 2).toUpperCase()}
                          </span>
                          <span>
                            <strong>{employee.name}</strong>
                            <small>{employee.department || employee.email}</small>
                          </span>
                        </div>
                      </td>
                      <td>{formatMinutes(employee.contract_minutes_per_week)}</td>
                      <td>
                        <strong>{formatMinutes(leave?.current_minutes ?? 0)}</strong>
                      </td>
                      <td>{formatMinutes(atvMinutes)}</td>
                      <td>{formatMinutes(leave?.transport_minutes ?? 0)}</td>
                      <td>{formatMinutes(leave?.planned_minutes ?? 0)}</td>
                      <td>{formatMinutes(leave?.requested_minutes ?? 0)}</td>
                      <td>{employee.managers.join(", ") || "-"}</td>
                      <td>
                        <span className={`member-status status-${employee.status}`}>
                          {employee.status === "active" ? "Actief" : employee.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </>
      )}

      {view === "reports" && reports && (
        <>
          <section className="metric-grid report-metrics">
            <article className="metric-card">
              <span className="metric-icon">
                <BarChart3 size={20} />
              </span>
              <div>
                <span>Gemiddelde verlofopname</span>
                <strong>{formatMinutes(reports.average_leave_minutes)}</strong>
                <small>Per actieve medewerker</small>
              </div>
            </article>
            <article className="metric-card">
              <span className="metric-icon metric-amber">
                <FileClock size={20} />
              </span>
              <div>
                <span>Openstaande aanvragen</span>
                <strong>{reports.open_requests}</strong>
                <small>Vereisen aandacht</small>
              </div>
            </article>
            <article className="metric-card">
              <span className="metric-icon metric-violet">
                <TrendingUp size={20} />
              </span>
              <div>
                <span>Verloftypen</span>
                <strong>{reports.leave_by_type.length}</strong>
                <small>Met gebruik in {year}</small>
              </div>
            </article>
            <article className="metric-card">
              <span className="metric-icon metric-mint">
                <AlertTriangle size={20} />
              </span>
              <div>
                <span>Vervallend transport</span>
                <strong>{reports.expiring_transport.length}</strong>
                <small>Binnen 90 dagen</small>
              </div>
            </article>
          </section>

          <div className="report-grid">
            <section className="surface chart-surface">
              <div className="surface-heading">
                <h2>Verlofgebruik per maand</h2>
              </div>
              <div className="bar-chart" aria-label="Verlofuren per maand">
                {reports.leave_usage_per_month.map((row, index) => (
                  <div
                    className="bar-column"
                    key={row.month}
                    title={`${row.hours} uur`}
                  >
                    <span className="bar-value">{row.hours || ""}</span>
                    <i
                      style={{
                        height: `${Math.max(3, (row.minutes / maxMonthly) * 100)}%`,
                      }}
                    />
                    <small>{months[index]}</small>
                  </div>
                ))}
              </div>
            </section>
            <section className="surface chart-surface">
              <div className="surface-heading">
                <h2>Verlof per type</h2>
              </div>
              <div className="horizontal-bars">
                {reports.leave_by_type.map((row) => {
                  const max = Math.max(
                    ...reports.leave_by_type.map((item) => item.minutes),
                    1,
                  );
                  return (
                    <div key={row.leave_type}>
                      <span>{row.leave_type}</span>
                      <div>
                        <i style={{ width: `${(row.minutes / max) * 100}%` }} />
                      </div>
                      <strong>{row.hours} u</strong>
                    </div>
                  );
                })}
              </div>
            </section>
            <section className="surface report-list-surface">
              <div className="surface-heading">
                <h2>Afwezigheid per team</h2>
              </div>
              {reports.absence_by_team.map((row) => (
                <div className="report-list-row" key={row.team}>
                  <span>{row.team}</span>
                  <strong>{row.hours} uur</strong>
                </div>
              ))}
            </section>
            <section className="surface report-list-surface">
              <div className="surface-heading">
                <h2>Vervallend transport</h2>
              </div>
              {reports.expiring_transport.length === 0 && (
                <p className="empty-copy">Geen transport binnen 90 dagen.</p>
              )}
              {reports.expiring_transport.map((row) => (
                <div className="report-list-row" key={row.id}>
                  <span>
                    <strong>{row.employee}</strong>
                    <small>
                      {row.leave_type} · {formatDate(row.expires_on)}
                    </small>
                  </span>
                  <strong>{formatMinutes(row.remaining_minutes)}</strong>
                </div>
              ))}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
